import { Injectable } from "@angular/core";
import { OnPremiseSyncDataState } from "./on-premise-sync-state.service";
import { concatMap, defer, from, map, mergeMap, Observable, of, retry, tap, toArray } from "rxjs";
import { toInspectionImageCreate } from "../sync-mapper";
import { InspectionImageDbService } from "../../database/inspection-image-db.service";
import { InspectionImageService } from "../../http-api/inspection-image.service";
import { FileSystemAccessService } from "../../file-system-access.service";
import { InspectionDbService } from "../../database/inspection-db.service";
import { ImageUploadService } from "../../image-upload.service";
import { InspectionImage, InspectionImageCreate, InspectionImageInfo, ReadImageOption, UploadOption } from "../../../_objects/inspection-image";
import { InspectionImageSchema } from "../../../_offline/models/inspectionImage.schema";
import { ImageConflictResponse } from "../../responses/inspection-image.response";
import { INSPECTION_IMAGE_STATUS_CODE } from "../../../app.constants";

/** A local image with the creation request built from it */
type ImageCreation = { schema: InspectionImageSchema; creation: InspectionImageCreate };

/** A created image or a conflict, named after the creation request it answers */
type CreationAnswer = Pick<InspectionImageInfo, "originalFileName" | "folderName">;

/** How many uploaded images are read at once */
const READ_CONCURRENCY = 4;

@Injectable({
  providedIn: "root"
})
export class InspectionImageFirstSyncService {
  /** A read that leaves the image uncompleted is tried again this many times, this far apart (the tests shorten the wait) */
  static readRetry = { count: 5, delayMs: 2000 };

  constructor(
    private syncState: OnPremiseSyncDataState,
    private inspectionDbService: InspectionDbService,
    private inspectionImageDbService: InspectionImageDbService,
    private inspectionImageService: InspectionImageService,
    private fileSystemAccessService: FileSystemAccessService,
    private uploadService: ImageUploadService
  ) {}

  /**
   * Creates the images of every local inspection in its cloud counterpart, uploads their files and waits for the cloud
   * to have read each of them, so that the retouches and measurements pushed next are accepted.
   */
  createInspectionImages(): Observable<InspectionImageInfo[]> {
    return this.inspectionDbService.getAllOnPremise().pipe(
      concatMap((inspection) => inspection),
      mergeMap((inspection) => this.createImagesForInspection(inspection.id)),
      mergeMap((createdImages) => createdImages),
      toArray()
    );
  }

  private createImagesForInspection(inspectionId: string): Observable<InspectionImageInfo[]> {
    const createdInspectionId = this.syncState.getInspectionId(inspectionId);

    return this.inspectionImageDbService.getAllOnPremise(inspectionId).pipe(
      mergeMap((imageSchemas) =>
        from(imageSchemas).pipe(
          mergeMap((imageSchema) => this.toImageCreation(imageSchema, createdInspectionId)),
          toArray()
        )
      ),
      mergeMap((images) =>
        from(toDistinctNameBatches(images)).pipe(
          concatMap((batch) => this.createAndUploadBatch(createdInspectionId, batch)),
          toArray(),
          map((batches) => batches.flat())
        )
      )
    );
  }

  private toImageCreation(schema: InspectionImageSchema, inspectionId: string): Observable<ImageCreation> {
    return from(this.fileSystemAccessService.getFile(schema.localFilePath, schema.fileName, schema.mimeType)).pipe(
      map((file) => ({ schema, creation: toInspectionImageCreate(schema, inspectionId, file) }))
    );
  }

  /** Creates the images of one batch, uploads their files, then has the cloud read each of them */
  private createAndUploadBatch(inspectionId: string, batch: ImageCreation[]): Observable<InspectionImageInfo[]> {
    const creations = batch.map((image) => image.creation);

    return this.createImages(inspectionId, creations).pipe(
      tap((createdImages) => this.rememberCreatedIds(batch, createdImages)),
      mergeMap((createdImages) => this.uploadService.uploadImageToBlobUrl(creations, createdImages).pipe(map(() => createdImages))),
      mergeMap((createdImages) => this.readImages(inspectionId, createdImages))
    );
  }

  /**
   * The cloud answers an image whose name is already taken with a conflict instead of creating it. Such an image is
   * created again under the next free name (keep both), so that no local image is left behind.
   */
  private createImages(inspectionId: string, creations: InspectionImageCreate[]): Observable<InspectionImageInfo[]> {
    return this.inspectionImageService.createInspectionImages(inspectionId, creations).pipe(
      mergeMap((response) => {
        const conflicted = toKeepBothCreations(response.imageConflicts ?? [], creations);
        if (conflicted.length === 0) {
          return of(response.createdImages);
        }

        return this.inspectionImageService.createInspectionImages(inspectionId, conflicted).pipe(
          map((retried) => {
            if (retried.imageConflicts?.length) {
              const names = retried.imageConflicts.map((conflict) => conflict.originalFileName).join(", ");
              throw new Error(`The cloud inspection already holds images named ${names} and could not keep both`);
            }
            return [...response.createdImages, ...retried.createdImages];
          })
        );
      })
    );
  }

  /** Keeps the cloud id of each local image. The names are distinct within a batch, so the match is unambiguous. */
  private rememberCreatedIds(batch: ImageCreation[], createdImages: InspectionImageInfo[]): void {
    createdImages.forEach((created) => {
      const image = batch.find(({ creation }) => answers(created, creation));
      if (image) {
        this.syncState.updateInspectionImage(created.imageId, image.schema.id);
      }
    });
  }

  /**
   * The cloud reads an uploaded file on its own, later (EXIF, thumbnail, status Completed), and refuses the retouches
   * and measurements of an image it has not read yet. Reading each image here runs that processing right away and
   * waits for it, as the online upload and the offline push do.
   */
  private readImages(inspectionId: string, createdImages: InspectionImageInfo[]): Observable<InspectionImageInfo[]> {
    return from(createdImages).pipe(
      mergeMap((image) => this.readImage(inspectionId, image), READ_CONCURRENCY),
      toArray(),
      map(() => createdImages)
    );
  }

  private readImage(inspectionId: string, image: InspectionImageInfo): Observable<InspectionImage> {
    const { count, delayMs } = InspectionImageFirstSyncService.readRetry;

    return defer(() => this.inspectionImageService.readInspectionImage(inspectionId, image.imageId, ReadImageOption.Upload)).pipe(
      map((readImage) => {
        if (readImage?.status !== INSPECTION_IMAGE_STATUS_CODE.COMPLETED) {
          throw new Error(`The cloud has not finished processing the image ${image.originalFileName}`);
        }
        return readImage;
      }),
      retry({ count, delay: delayMs })
    );
  }
}

/** Whether the cloud's answer (a created image or a conflict) is the one to this creation request */
const answers = (answer: CreationAnswer, creation: InspectionImageCreate): boolean =>
  answer.originalFileName === creation.fileName && (answer.folderName ?? null) === (creation.folderName ?? null);

const isSameName = (a: InspectionImageCreate, b: InspectionImageCreate): boolean =>
  a.fileName?.toLowerCase() === b.fileName?.toLowerCase() && (a.folderName ?? null) === (b.folderName ?? null);

/**
 * Batches in which no two images share a name in the same folder. The cloud creates the images of a request in
 * parallel and renames a duplicate only when it can already see the image it duplicates, so same-named images go in
 * successive requests.
 */
const toDistinctNameBatches = (images: ImageCreation[]): ImageCreation[][] => {
  const batches: ImageCreation[][] = [];
  for (const image of images) {
    const batch = batches.find((candidate) => !candidate.some((other) => isSameName(other.creation, image.creation)));
    if (batch) {
      batch.push(image);
    } else {
      batches.push([image]);
    }
  }
  return batches;
};

/** The creations the cloud reported as conflicts, to be sent again keeping both images */
const toKeepBothCreations = (conflicts: ImageConflictResponse[], creations: InspectionImageCreate[]): InspectionImageCreate[] =>
  conflicts
    .map((conflict) => creations.find((creation) => answers(conflict, creation)))
    .filter((creation): creation is InspectionImageCreate => !!creation)
    .map((creation) => ({ ...creation, uploadOption: UploadOption.KeepBoth }));
